import { apiRequest } from './axiosConfig';

export interface MagicResizeDebugRequest {
  image_id: number;
  target_sizes: Array<{ width: number; height: number }>;
}

export interface GeminiStepResult {
  attempted: boolean;
  success?: boolean;
  prompt?: string | null;
  canvas_size?: { width: number; height: number } | null;
  result_size?: { width: number; height: number } | null;
  result_aspect_ratio?: number | null;
  aspect_ratio_deviation_percent?: number | null;
  needed_center_crop_correction?: boolean;
  usage_metadata?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    image_tokens: number;
  } | null;
  processing_time_ms?: number;
  image_base64?: string | null;
  error?: string | null;
  reason?: string | null;
  final_crop?: FinalVersionResult;
  final_stretch?: FinalVersionResult;
}

export interface GeminiBlueMarginStepResult {
  attempted: boolean;
  success?: boolean;
  prompt?: string | null;
  canvas_size?: { width: number; height: number } | null;
  content_area?: { x: number; y: number; width: number; height: number } | null;
  raw_result_size?: { width: number; height: number } | null;
  raw_image_base64?: string | null;
  cropped_result_size?: { width: number; height: number } | null;
  cropped_image_base64?: string | null;
  result_aspect_ratio?: number | null;
  aspect_ratio_deviation_percent?: number | null;
  canvas_image_base64?: string | null;
  usage_metadata?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    image_tokens: number;
  } | null;
  processing_time_ms?: number;
  error?: string | null;
  reason?: string | null;
  final_crop?: FinalVersionResult;
  final_stretch?: FinalVersionResult;
}

export interface GeminiARConfigStepResult {
  attempted: boolean;
  success: boolean;
  image_base64?: string;
  result_size?: { width: number; height: number };
  ar_deviation?: number;
  closest_gemini_ratio?: string;
  closest_gemini_ratio_value?: number;
  prompt?: string;
  canvas_size?: string;
  processing_time_s?: number;
  usage_metadata?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    image_tokens: number;
  };
  error?: string;
  final_crop?: FinalVersionResult;
  final_stretch?: FinalVersionResult;
}

export interface GeminiImageOnlyStepResult {
  attempted: boolean;
  success: boolean;
  image_base64?: string;
  result_size?: { width: number; height: number };
  ar_deviation?: number;
  closest_gemini_ratio?: string;
  closest_gemini_ratio_value?: number;
  prompt?: string;
  processing_time_s?: number;
  usage_metadata?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    image_tokens: number;
  };
  error?: string;
  final_crop?: FinalVersionResult;
  final_stretch?: FinalVersionResult;
}

export interface CenterCropStepResult {
  attempted: boolean;
  result_size?: { width: number; height: number } | null;
  crop_direction?: string | null;
  crop_offset?: { x: number; y: number } | null;
  processing_time_ms?: number;
  image_base64?: string | null;
  error?: string | null;
}

export interface FinalVersionResult {
  success: boolean;
  image_base64?: string | null;
  width?: number | null;
  height?: number | null;
  crop_region?: string | null;
  source_size?: string | null;
  error?: string | null;
}

export interface FinalResizeStepResult {
  result_size: { width: number; height: number } | null;
  processing_time_ms: number;
  image_base64: string | null;
  error?: string | null;
}

export interface DebugSizeResult {
  target_size: { width: number; height: number };
  target_aspect_ratio: number;
  source_aspect_ratio: number;
  aspect_ratio_differs: boolean;
  strategy_used: string;
  steps: {
    gemini: GeminiStepResult;
    gemini_blue_margin?: GeminiBlueMarginStepResult;
    gemini_ar_config?: GeminiARConfigStepResult;
    gemini_image_only?: GeminiImageOnlyStepResult;
    center_crop: CenterCropStepResult;
    final_resize: FinalResizeStepResult;
  };
  total_processing_time_ms: number;
  error: string | null;
}

export interface MagicResizeDebugResponse {
  success: boolean;
  source_image: {
    id: number;
    width: number;
    height: number;
    aspect_ratio: number;
    file_size: number;
    image_base64: string;
  };
  results: DebugSizeResult[];
  error?: string;
}

// History types
export interface MethodSummary {
  success: boolean;
  ar_deviation?: number;
}

export interface ResultSummaryItem {
  target: string;
  methods: {
    gemini?: MethodSummary;
    gemini_blue_margin?: MethodSummary;
    gemini_ar_config?: MethodSummary;
    gemini_image_only?: MethodSummary;
    center_crop?: MethodSummary;
  };
}

export interface DebugHistoryItem {
  id: number;
  image_id: number;
  source_width: number;
  source_height: number;
  target_sizes: Array<{ width: number; height: number }>;
  result_summary: { results: ResultSummaryItem[] };
  total_processing_time_ms: number | null;
  created_at: string;
}

export interface DebugHistoryListResponse {
  data: DebugHistoryItem[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface DebugHistoryDetailResponse {
  id: number;
  image_id: number;
  source_width: number;
  source_height: number;
  target_sizes: Array<{ width: number; height: number }>;
  result_summary: { results: ResultSummaryItem[] };
  total_processing_time_ms: number | null;
  created_at: string;
  result_data: MagicResizeDebugResponse;
}

const adminMagicResizeDebugService = {
  async executeDebug(data: MagicResizeDebugRequest): Promise<MagicResizeDebugResponse> {
    return apiRequest.post<MagicResizeDebugResponse>(
      'admin/magic-resize/debug',
      data,
      { timeout: 300000 }
    );
  },

  async getHistory(page: number = 1): Promise<DebugHistoryListResponse> {
    return apiRequest.get<DebugHistoryListResponse>(
      `admin/magic-resize/debug/history?page=${page}`
    );
  },

  async getHistoryDetail(id: number): Promise<DebugHistoryDetailResponse> {
    return apiRequest.get<DebugHistoryDetailResponse>(
      `admin/magic-resize/debug/history/${id}`,
      { timeout: 120000 }
    );
  },

  async deleteHistory(id: number): Promise<void> {
    return apiRequest.delete(`admin/magic-resize/debug/history/${id}`);
  },
};

export default adminMagicResizeDebugService;
